import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { DataNode } from 'components/WorkspaceEditor/types'
import { withDebounce } from 'components/WorkspaceEditor/utils'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useProcessSuggestionCards } from './useProcessSuggestionCards'
import { useTrackChangeCards } from './useTrackChangeCards'

const DEBOUNCE_DELAY = 500

/**
 * Effect hook to process suggestion cards on the tree.
 * Optimized for performance with memoization and stable references.
 *
 * @param treeData - The current tree data
 * @param showLoadingState - Indicates whether the loading state is shown
 * @param articlesLoading - Array of article IDs that are currently loading
 */
export const useProcessSuggestionCardsOnTreeEffect = (
    treeData: DataNode[],
    showLoadingState: boolean,
    articlesLoading: string[]
) => {
    const { isNotDefaultSuggestionCardFilter } = useTrackChangeCards()
    const { processSuggestionCards } = useProcessSuggestionCards()

    const contentItemViewNodes = useShallowSelector(
        (s) => s.contentItemViewNodes
    )
    const suggestionData = useSuggestionsShallowSelector(
        (s) => s.suggestionData
    )

    const treeDataRef = useRef<DataNode[]>(treeData)
    const processSuggestionCardsRef = useRef(processSuggestionCards)
    const contentItemViewNodesRef = useRef(contentItemViewNodes)

    processSuggestionCardsRef.current = processSuggestionCards
    contentItemViewNodesRef.current = contentItemViewNodes

    const articlesLoadingState = useMemo(() => {
        const length = articlesLoading.length
        return {
            length,
            hasDefault: articlesLoading.includes('default'),
            isEmpty: length === 0,
            isOnlyDefault: length === 1 && articlesLoading[0] === 'default',
        }
    }, [articlesLoading])

    const shouldProcessConditions = useMemo(() => {
        const isDefaultFilterActive = !isNotDefaultSuggestionCardFilter
        return {
            isDefaultFilterActive,
            shouldProcess:
                isDefaultFilterActive &&
                (!showLoadingState ||
                    articlesLoadingState.isOnlyDefault ||
                    articlesLoadingState.isEmpty),
            hasSuggestionData:
                suggestionData && Object.keys(suggestionData).length > 0,
        }
    }, [
        isNotDefaultSuggestionCardFilter,
        showLoadingState,
        articlesLoadingState.isOnlyDefault,
        articlesLoadingState.isEmpty,
        suggestionData,
    ])

    const debouncedProcessSuggestionCards = useMemo(
        () =>
            withDebounce(async () => {
                processSuggestionCardsRef.current(
                    contentItemViewNodesRef.current
                )
            }, DEBOUNCE_DELAY),
        []
    )

    const processImmediately = useCallback(() => {
        processSuggestionCards(contentItemViewNodes)
    }, [processSuggestionCards, contentItemViewNodes])

    useEffect(() => {
        const { shouldProcess, hasSuggestionData } = shouldProcessConditions
        const isTreeDataChanged = treeDataRef.current !== treeData

        if (!shouldProcess) return

        if (hasSuggestionData) {
            if (isTreeDataChanged) {
                treeDataRef.current = treeData
                debouncedProcessSuggestionCards()
            }
        } else {
            processImmediately()
        }
    }, [
        shouldProcessConditions,
        treeData,
        debouncedProcessSuggestionCards,
        processImmediately,
    ])

    useEffect(() => {
        return () => {
            if (debouncedProcessSuggestionCards.cancel) {
                debouncedProcessSuggestionCards.cancel()
            }
        }
    }, [debouncedProcessSuggestionCards])
}
